import type {
  VisualPayload,
  SeasonOverview,
  NewMovesGroup,
  NewMove,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  TYPE_CHIP_CLASSES,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * Focused render for new move acquisitions.
 *
 * Backend emits "new_move_acquisitions" with an items_grouped list,
 * grouped by pokemon_name.  When the user asked about a specific
 * Pokémon, there's typically one group; the dedicated render
 * spotlights its new moves with big type-colored chips.  When the
 * user asked broadly ("what new moves did Pokémon learn this season"),
 * the backend fills the grouped list with all 28-ish entries and we
 * fall through to a compact-list render.
 */
export default function NewMovesCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ?? {}) as SeasonOverview;
  const newMovesSec = sections.find((s) => s.section === "new_move_acquisitions");
  const groups = (newMovesSec?.items_grouped as NewMovesGroup[] | undefined) ?? [];

  if (groups.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
        <SeasonHeaderStrip overview={overview} />
        <p className="text-sm text-white/40 py-2">No new move acquisitions announced this season.</p>
        <SeasonSourceLink sourceUrl={payload.source_url} />
      </CardShell>
    );
  }

  // Focused render: one group → spotlight that single Pokémon with
  // big chips.  Multi-group → compact per-Pokémon row list.
  const isFocused = groups.length === 1;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-indigo-500/20">
      <SeasonHeaderStrip overview={overview} />

      {isFocused ? (
        <FocusedPokemon group={groups[0]} />
      ) : (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
            Pokémon with new moves ({groups.length})
          </p>
          <ul className="space-y-1.5">
            {groups.map((g, i) => (
              // Stack on narrow viewports so long Pokémon names + move lists
              // don't ellipsis-clip; sit side-by-side on wider screens.
              <li key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2 text-xs">
                <span className="text-white/85 font-medium sm:min-w-[7rem] shrink-0">{g.pokemon_name}</span>
                <span className="flex flex-wrap gap-1 sm:flex-1">
                  {g.moves.map((m, j) => (
                    <CompactMoveChip key={j} m={m} />
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}

function FocusedPokemon({ group }: { group: NewMovesGroup }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-4">
      <p className="text-base font-semibold text-white leading-tight mb-1">
        {group.pokemon_name}
      </p>
      <p className="text-[11px] text-white/40 mb-3">
        Learns {group.moves.length} new move{group.moves.length === 1 ? "" : "s"} this season
      </p>
      <div className="flex flex-wrap gap-2">
        {group.moves.map((m, i) => (
          <BigMoveChip key={i} m={m} />
        ))}
      </div>
    </div>
  );
}

function BigMoveChip({ m }: { m: NewMove }) {
  const cls = m.move_type ? TYPE_CHIP_CLASSES[m.move_type.toLowerCase()] : null;
  const cat = m.move_category === "fast" ? "Fast" : m.move_category === "charged" ? "Charged" : null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${
        cls ?? "bg-white/10 text-white/85 border-white/15"
      }`}
    >
      <span className="text-sm font-medium">{m.move_name ?? "?"}</span>
      {cat && (
        <span className="text-[10px] uppercase tracking-wide opacity-70 px-1 py-0.5 rounded bg-black/20">
          {cat}
        </span>
      )}
    </span>
  );
}

function CompactMoveChip({ m }: { m: NewMove }) {
  const cls = m.move_type ? TYPE_CHIP_CLASSES[m.move_type.toLowerCase()] : null;
  const cat = m.move_category === "fast" ? "f" : m.move_category === "charged" ? "c" : null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] border ${
        cls ?? "bg-white/10 text-white/85 border-white/15"
      }`}
    >
      <span>{m.move_name ?? "?"}</span>
      {cat && <span className="opacity-50 lowercase">{cat}</span>}
    </span>
  );
}
